<template>
  <div class="snags-table-component">
    <div class="d-flex mb-4">
      <a :href="exportCsvUrl" download class="ms-auto btn btn-success">Export CSV</a>
    </div>

    <div class="filters d-flex flex-wrap align-items-end">
      <div class="form-group me-3 mb-25">
        <label for="search" class="me-2">Search</label>
        <input v-model="search" type="text" class="form-control" id="search" @keyup.enter="getSnags()"
               :disabled="loading">
      </div>

      <div class="form-group me-3 mb-25">
        <label for="location" class="me-2">Location</label>
        <select v-model="location" name="location" id="location" class="form-control">
          <option :value="null">All</option>
          <option v-for="location in locations" :key="location.id" :value="location.id">{{
              location.name
            }}
          </option>
        </select>
      </div>

      <div class="form-group me-3 mb-25">
        <label for="category" class="me-2">Category</label>
        <select v-model="category" name="category" id="category" class="form-control">
          <option :value="null">All</option>
          <option v-for="category in categories" :key="category.id" :value="category.id">{{
              category.name
            }}
          </option>
        </select>
      </div>

      <div class="form-group me-3 mb-25">
        <label for="development" class="me-2">Development</label>
        <select v-model="development" name="development" id="development" class="form-control">
          <option :value="null">All</option>
          <option v-for="(development, developmentId) in developments" :key="developmentId" :value="developmentId">
            {{ development }}
          </option>
        </select>
      </div>

      <div v-if="filteredPlots.length > 0" class="form-group me-3 mb-25">
        <label for="plot" class="me-2">Plot</label>
        <select v-model="plot" name="plot" id="plot" class="form-control">
          <option :value="null">All</option>
          <option v-for="plot in filteredPlots" :key="plot.id" :value="plot.id">{{ plot.name_number }}</option>
        </select>
      </div>

      <div class="form-group me-3 mb-25">
        <label for="status" class="me-2">Status</label>
        <select v-model="status" name="status" id="status" class="form-control">
          <option :value="null">All</option>
          <option v-for="status in statuses" :key="status.id" :value="status.id">{{ status.name }}</option>
        </select>
      </div>
    </div>

    <div class="filters mb-4 d-flex flex-wrap align-items-center">
      <div class="form-group me-3 mb-25">
        <div class="form-check">
          <input v-model="hideRead" class="form-check-input" type="checkbox" :value="true" id="hide_read">
          <label class="form-check-label" for="hide_read">
            Hide Read
          </label>
        </div>
      </div>

      <div class="form-group me-3 mb-25">
        <div class="form-check">
          <input v-model="hideNonPriority" class="form-check-input" type="checkbox" :value="true"
                 id="hide_non_priority">
          <label class="form-check-label" for="hide_non_priority">
            Hide Non-Within 7 Days
          </label>
        </div>
      </div>

      <div class="form-group me-3 mb-25">
        <div class="form-check">
          <input v-model="hideResolved" class="form-check-input" type="checkbox" :value="true" id="hide_resolved">
          <label class="form-check-label" for="hide_resolved">
            Hide Resolved
          </label>
        </div>
      </div>

      <div class="form-group me-3 mb-25">
        <div class="form-check">
          <input v-model="hideRejected" class="form-check-input" type="checkbox" :value="true" id="hide_rejected">
          <label class="form-check-label" for="hide_rejected">
            Hide Rejected
          </label>
        </div>
      </div>

      <div class="form-group me-3 mb-25">
        <div class="form-check">
          <input v-model="hideMaintenance" class="form-check-input" type="checkbox" :value="true" id="hide_maintenance">
          <label class="form-check-label" for="hide_maintenance">
            Hide Maintenance
          </label>
        </div>
      </div>

      <button @click="getSnags()" type="button" class="btn btn-primary mb-25" :disabled="loading">
        Search
      </button>

      <button v-if="filters" @click="resetFilters()" type="button" class="ms-3 mb-25 btn btn-danger">Reset Filters
      </button>

      <div v-if="loading" class="ms-3 mb-3 spinner-border" role="status">
        <span class="sr-only">Loading...</span>
      </div>
    </div>

    <div class="table-responsive">
      <table class="table table-hover">
        <thead>
        <tr>
          <th class="text-center"><i class="far fa-envelope"></i></th>
          <th>Customer</th>
          <th>Location</th>
          <th>Category</th>
          <th>Development</th>
          <th>House Type</th>
          <th>Plot Ref</th>
          <th>Within 7 Days?</th>
          <th>Date Created</th>
          <th>Status</th>
          <th></th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="snag in snags.data" :key="snag.id" :class="{'is-priority': snag.is_priority}">
          <td class="text-center"><span v-if="snag.unread_messages_count > 0"
                                        class="badge badge-pill rounded-circle badge-danger">{{
              snag.unread_messages_count
            }}</span></td>
          <td>{{ snag.build.user.name }}</td>
          <td>
            {{ snag.version === 1 ? snag.old_location : snag.location.name }}
            <span v-if="snag.house_type_floorplan_id" class="text-muted">(Floorplan)</span>
          </td>
          <td>
            {{ snag.version === 1 ? '-' : snag.category.name }}
          </td>
          <td>{{ snag.build.development_house_type.development.name }}</td>
          <td>{{ snag.build.development_house_type.house_type.name }}</td>
          <td>{{ snag.build.plot.reference }}</td>
          <td>{{ snag.is_priority ? 'Yes' : 'No' }}</td>
          <td>{{ snag.created_at | moment("llll") }}</td>
          <td>
            <div class="badge badge-pill rounded-pill w-100 text-center p-2"
                 v-bind:style="{ backgroundColor: '#' + snag.internal_status.color }">
              {{ snag.internal_status ? snag.internal_status.name : 'None' }}
            </div>
          </td>
          <td><a :href="'/admin/snags/' + snag.id + '/edit'" class="btn btn-sm btn-primary">Edit</a></td>
        </tr>
        </tbody>
      </table>
    </div>
    <nav>
      <pagination :data="snags" @pagination-change-page="getSnags" :limit="5"></pagination>
    </nav>
    <p class="text-muted">Found a total of {{ Number(snags.total).toLocaleString() }} result(s).</p>
  </div>
</template>

<script>
export default {
  data() {
    return {
      loading: false,
      search: null,
      location: null,
      locations: null,
      category: null,
      categories: null,
      developments: null,
      development: null,
      plots: null,
      plot: null,
      status: null,
      hideRead: false,
      hideNonPriority: false,
      hideResolved: false,
      hideRejected: false,
      hideMaintenance: false,
      statuses: null,
      page: 1,
      snags: null,
      filters: null,
      exportCsvUrl: window.api_url + '/admin/snags/export_csv',
    };
  },

  props: {
    preloadSnags: {
      type: Object,
      default: null,
    },

    preloadDevelopments: {
      type: Object,
      default: null,
    },

    preloadPlots: {
      type: Array,
      default: null,
    },

    preloadLocations: {
      type: Array,
      default: null,
    },

    preloadCategories: {
      type: Array,
      default: null,
    },

    preloadStatuses: {
      type: Array,
      default: null,
    },
  },

  computed: {
    keyStatuses: function () {
      return this.statuses.filter(status => status.id <= 5 || status.id >= 15);
    },

    filteredPlots: function () {
      if (this.development) {
        return this.plots.filter(plot => plot.development_id == this.development);
      }

      return [];
    },
  },

  created() {
    this.snags = this.preloadSnags;
    this.locations = this.preloadLocations;
    this.categories = this.preloadCategories;
    this.developments = this.preloadDevelopments;
    this.plots = this.preloadPlots;
    this.statuses = this.preloadStatuses;

    // Load get params.
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('filters[search]')) {
      this.search = urlParams.get('filters[search]');
    }

    if (urlParams.get('filters[location]')) {
      this.location = urlParams.get('filters[location]');
    }

    if (urlParams.get('filters[development]')) {
      this.development = urlParams.get('filters[development]');
    }

    if (urlParams.get('filters[plot]')) {
      this.plot = urlParams.get('filters[plot]');
    }

    if (urlParams.get('filters[status]')) {
      this.status = urlParams.get('filters[status]');
    }

    if (urlParams.get('filters[hideRead]')) {
      this.hideRead = urlParams.get('filters[hideRead]') === 'true';
    }

    if (urlParams.get('filters[hideNonPriority]')) {
      this.hideNonPriority = urlParams.get('filters[hideNonPriority]') === 'true';
    }

    if (urlParams.get('filters[hideResolved]')) {
      this.hideResolved = urlParams.get('filters[hideResolved]') === 'true';
    }

    if (urlParams.get('filters[hideRejected]')) {
      this.hideRejected = urlParams.get('filters[hideRejected]') === 'true';
    }

    if (urlParams.get('filters[hideMaintenance]')) {
      this.hideMaintenance = urlParams.get('filters[hideMaintenance]') === 'true';
    }

    this.setFilters();
  },

  methods: {
    setFilters() {
      if (this.search !== null || this.location !== null || this.development !== null || this.status !== null || this.hideRead !== false || this.hideNonPriority !== false || this.hideResolved !== false || this.hideRejected !== false || this.hideMaintenance !== false || this.plot !== null) {
        this.filters = {
          search: this.search,
          location: this.location,
          development: this.development,
          plot: this.plot,
          status: this.status,
          hideRead: this.hideRead ? 'true' : 'false',
          hideNonPriority: this.hideNonPriority ? 'true' : 'false',
          hideResolved: this.hideResolved ? 'true' : 'false',
          hideRejected: this.hideRejected ? 'true' : 'false',
          hideMaintenance: this.hideMaintenance ? 'true' : 'false',
        };
      } else {
        this.filters = null;
      }
    },

    handlePushState() {
      let pageUrl = '?';

      // Add filters.
      for (const filter in this.filters) {
        if (this.filters[filter] === null) {
          continue;
        }

        if (pageUrl !== '?') {
          pageUrl += '&';
        }

        pageUrl += `filters[${filter}]=${encodeURIComponent(this.filters[filter])}`;
      }

      // Add page number.
      if (this.page > 1) {
        pageUrl += '&page=' + this.page;
      }

      window.history.pushState('', '', pageUrl);
    },

    getSnags(page = 1) {
      this.loading = true;
      this.page = page;
      this.setFilters();

      this.handlePushState();

      axios.post(`${window.api_url}/admin/snags/get`, {
        page: this.page,
        filters: this.filters,
      })
        .then(res => {
          this.snags = res.data.snags;
        })
        .catch(err => {
          this.loading = false;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    resetFilters() {
      this.search = null;
      this.location = null;
      this.hideRead = false;
      this.hideNonPriority = false;
      this.hideResolved = false;
      this.hideRejected = false;
      this.hideMaintenance = false;
      this.development = null;
      this.plot = null;
      this.status = null;
      this.getSnags();
    },
  },
};
</script>
